import React, { useState, useEffect } from 'react';
import { fetchTaps } from '../../api/fetchTaps';
import { happyHourConfig } from '../../data/happyHourConfig';
import { siteConfig } from '../../data/siteConfig';
import TapCard from '../../components/TapCard/TapCard';
import LoaderBeer from '../../components/LoaderBeer/LoaderBeer';
import './Menu.css';

const Menu = () => {
  const [taps, setTaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Check if currently in happy hour
  const isHappyHour = () => {
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-AU', { weekday: "long" }).toLowerCase();;
    const currentTime = now.toLocaleTimeString('en-AU', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    const isHappyHourDay = happyHourConfig.schedule.days.includes(currentDay);
    const isHappyHourTime = currentTime >= happyHourConfig.schedule.startTime && 
                           currentTime <= happyHourConfig.schedule.endTime;

    return isHappyHourDay && isHappyHourTime;
  };

  // Load tap data
  const loadTaps = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchTaps();
      
      if (response.success) {
        setTaps(response.data);
        setLastRefresh(new Date());
      } else {
        setError(response.error || 'Failed to load tap data');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error loading taps:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadTaps();
  }, []);

  // Auto refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadTaps, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter available and unavailable taps
  const availableTaps = taps.filter(tap => tap.isAvailable);
  const upcomingTaps = taps.filter(tap => !tap.isAvailable);

  if (loading && taps.length === 0) {
    return (
      <div className="menu">
        <div className="container">
          <LoaderBeer size="large" message="Loading Tap List..." />
        </div>
      </div>
    );
  }

  return (
    <div className="menu">
      <div className="container">
        {/* Header */}
        <div className="menu__header">
          <h1 className="menu__title">Tap List</h1>
          <p className="menu__subtitle">
            {siteConfig.features.tapsCount} Rotating Taps featuring {siteConfig.features.focus}
          </p>
          
          {/* Status Bar */}
          <div className="menu__status">
            <div className="status-item">
              <span className="status-label">Available Taps:</span>
              <span className="status-value">{availableTaps.length}</span>
            </div>
            
            {isHappyHour() && (
              <div className="status-item status-item--happy-hour">
                <span className="status-label">🍺 {happyHourConfig.offer.title} Active</span>
                <span className="status-value">{happyHourConfig.offer.shortDescription}</span>
              </div>
            )}
            
            {lastRefresh && (
              <div className="status-item">
                <span className="status-label">Last Updated:</span>
                <span className="status-value">
                  {lastRefresh.toLocaleTimeString('en-AU', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Refresh Button */}
          <div className="menu__actions">
            <button 
              onClick={loadTaps}
              disabled={loading}
              className="btn btn-secondary"
            >
              {loading ? 'Refreshing...' : 'Refresh Menu'}
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="error-message">
            <p>⚠️ {error}</p>
            <button onClick={loadTaps} className="btn btn-primary btn-sm">
              Try Again
            </button>
          </div>
        )}

        {/* Available Taps */}
        {availableTaps.length > 0 ? (
          <section className="menu__section">
            <h2 className="section__title">On Tap Now</h2>
            <div className="taps-grid">
              {availableTaps.map(tap => (
                <TapCard 
                  key={tap.tapNumber} 
                  beer={tap} 
                  showHappyHour={isHappyHour()}
                />
              ))}
            </div>
          </section>
        ) : !loading && (
          <section className="menu__section">
            <div className="empty-state">
              <p>No taps available at the moment. Please check back soon!</p>
            </div>
          </section>
        )}

        {/* Coming Soon */}
        {upcomingTaps.length > 0 && (
          <section className="menu__section">
            <h2 className="section__title">Coming Soon</h2>
            <div className="taps-grid">
              {upcomingTaps.map(tap => (
                <TapCard 
                  key={tap.tapNumber} 
                  beer={tap} 
                  showHappyHour={false}
                />
              ))}
            </div>
          </section>
        )}

        {/* Happy Hour Info */}
        {happyHourConfig.display.showBadge && (
          <section className="menu__section">
            <div className="happy-hour-info">
              <h3>🍺 Happy Hour</h3>
              <div className="happy-hour-details">
                <p><strong>{happyHourConfig.offer.title}:</strong> {happyHourConfig.offer.description}</p>
                <p><strong>When:</strong> {happyHourConfig.schedule.days.map(day => 
                  day.charAt(0).toUpperCase() + day.slice(1)
                ).join(', ')} from {happyHourConfig.schedule.startTime} - {happyHourConfig.schedule.endTime}</p>
                <p><strong>Valid on:</strong> Pot and Schooner sizes</p>
                <p className="terms">*Cannot be combined with other offers. Subject to availability.</p>
              </div>
              
              {!isHappyHour() && (
                <div className="next-happy-hour">
                  <p>Next Happy Hour: Check back during weekday afternoons!</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Food Policy */}
        <section className="menu__section">
          <div className="food-policy">
            <h3>🥘 {siteConfig.features.concept}</h3>
            <p>
              Bring your favorite food from home or order from nearby restaurants. 
              Enjoy your meal with our premium selection of Victorian craft beers 
              in our welcoming, family-friendly atmosphere.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Menu;